import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

export type ApiResponse_Delete_Reply = {
  id: number;
  post_id: number;
};

const METHOD = 'DELETE';
const ENDPOINT = 'posts/replies/{replyId}';

export const useDeleteReply = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      params: { replyId: number; postId: number; communityId?: number; groupId?: number }
    ) => {
      try {
        const { replyId } = params;
        const data = await handleApiCall<ApiResponse_Delete_Reply, Record<string, never>>(
          session,
          METHOD,
          getResolvedEndpoint(ENDPOINT, {
            replyId: replyId.toString(),
          }),
          {}
        );

        enqueueSnackbar('Reply deleted successfully!', { variant: 'success' });
        return data;
      } catch (err) {
        enqueueSnackbar('Failed to delete reply.', { variant: 'error' });
        throw err;
      }
    },
    onSuccess: (data, variables) => {
      // Invalidate post replies
      queryClient.invalidateQueries({
        queryKey: ['posts', variables.postId, 'replies'],
      });
      // Invalidate post to update reply count
      queryClient.invalidateQueries({
        queryKey: ['posts', variables.postId],
      });
      // Invalidate post lists to update reply counts
      if (variables.communityId) {
        queryClient.invalidateQueries({
          queryKey: ['posts', 'community', variables.communityId],
        });
      }
      if (variables.groupId) {
        queryClient.invalidateQueries({
          queryKey: ['posts', 'group', variables.groupId],
        });
      }
      // Remove the specific reply from cache
      queryClient.removeQueries({
        queryKey: ['posts', 'replies', data.id],
      });
    },
  });
};